import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Assume these icons are imported from an icon library
import { useSidebar } from '../context/SidebarContext';
import { ExperienceSwitcher } from '../design-v2/hotel/shell/ExperienceSwitcher';
import { useOnboardingNavFlags } from '../features/onboarding/useOnboardingChecklist';
import {
  //   BoxCubeIcon,
  BoxIcon,
  CalenderIcon,
  ChatIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  InfoIcon,
  ListIcon,
  // PageIcon,
  PieChartIcon,
  PlugInIcon,
  TableIcon,
  BoltIcon,
  PhoneIcon,
  VoicemailIcon,
} from '../icons';
import { useNavVisibility } from './useNavVisibility';
// import SidebarWidget from './SidebarWidget';

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  allowedRoles?: ('Owner' | 'Manager' | 'Staff')[];
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems: NavItem[] = [
  {
    icon: <BoxIcon />,
    name: 'Onboarding',
    path: '/onboarding',
  },
  {
    icon: <CheckCircleIcon />,
    name: 'Getting started',
    path: '/getting-started',
  },
  {
    icon: <GridIcon />,
    name: 'Dashboard',
    path: '/',
  },
  {
    icon: <InfoIcon />,
    name: 'Store Info',
    path: '/store-info',
  },
  {
    icon: <PhoneIcon />,
    name: 'Calls',
    path: '/calls',
  },
  {
    icon: <VoicemailIcon />,
    name: 'Voicemail',
    path: '/voicemail',
  },
  {
    icon: <ChatIcon />,
    name: 'SMS',
    path: '/sms',
  },
  {
    name: 'Orders',
    icon: <TableIcon />,
    path: '/orders',
  },
  {
    name: 'Menu Availability',
    icon: <ListIcon />,
    path: '/menu-availability',
  },
  {
    icon: <CalenderIcon />,
    name: 'Reservations',
    path: '/reservations',
  },
  {
    icon: <CalenderIcon />,
    name: 'Events',
    path: '/special-events',
  },
  {
    icon: <CalenderIcon />,
    name: 'Events Platform',
    path: '/events',
  },
  {
    icon: <BoxIcon />,
    name: 'Catering',
    path: '/catering',
  },
  {
    name: 'Customers',
    icon: <ListIcon />,
    path: '/customer-tables',
  },
  {
    icon: <PlugInIcon />,
    name: 'Integrations',
    path: '/integrations',
  },
  {
    icon: <PieChartIcon />,
    name: 'Sales',
    path: '/sales/prospects',
    allowedRoles: [], // Only platform admins can access via platformAdminOnly check
  },
  {
    icon: <BoltIcon />,
    name: 'Platform Admin',
    path: '/platform-admin',
    allowedRoles: [], // Only platform admins can access via platformAdminOnly check
  },
];

/**
 * Before the store is live the sidebar is just setup and the pages it needs,
 * plus staff tools (gated below).
 */
const BEFORE_LIVE_PATHS = [
  '/onboarding',
  '/getting-started',
  '/',
  '/store-info',
  '/testing',
  '/platform-admin',
  '/sales/prospects',
];

const othersItems: NavItem[] = [
  {
    icon: <PieChartIcon />,
    name: 'Settings',
    subItems: [{ name: 'Knowledge Base', path: '/knowledge-base', pro: false }],
  },
];

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const {
    isPlatformAdmin,
    role,
    showSms,
    showMenuAvailability,
    showEvents,
    showCatering,
    showEventsPlatform,
    showGroupOnboarding,
  } = useNavVisibility();
  const { showSetup, setupOnly } = useOnboardingNavFlags();

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: 'main' | 'others';
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {},
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) =>
      location.pathname === path ||
      ([
        '/platform-admin',
        '/reservations',
        '/catering',
        '/events',
        '/store-info',
      ].includes(path) &&
        location.pathname.startsWith(`${path}/`)),
    [location.pathname],
  );

  useEffect(() => {
    let submenuMatched = false;
    ['main', 'others'].forEach((menuType) => {
      const items = menuType === 'main' ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as 'main' | 'others',
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: 'main' | 'others') => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: 'main' | 'others') => {
    return (
      <ul className="flex flex-col gap-4">
        {items
          .filter((item) => {
            if (setupOnly && !BEFORE_LIVE_PATHS.includes(item.path ?? '')) {
              return false;
            }
            // Special case for testing, sales, and platform admin pages - only platform admins
            if (
              item.path === '/testing' ||
              item.path === '/platform-admin' ||
              item.path === '/sales/prospects'
            ) {
              return isPlatformAdmin;
            }
            // The group's own people; an admin only while the group has work
            if (item.path === '/onboarding') {
              return showGroupOnboarding;
            }
            // Shown while the checklist has steps left to complete
            if (item.path === '/getting-started') {
              return showSetup;
            }
            // SMS Inbox is only shown when the store has it enabled
            if (item.path === '/sms') {
              return showSms;
            }
            if (item.path === '/menu-availability') {
              return showMenuAvailability;
            }
            if (item.path === '/special-events') {
              return showEvents;
            }
            if (item.path === '/events') {
              return showEventsPlatform;
            }
            if (item.path === '/catering') {
              return showCatering;
            }
            // Regular role-based filtering for other items
            return (
              !item.allowedRoles || (role && item.allowedRoles.includes(role))
            );
          })
          .map((nav, index) => (
            <li key={nav.name}>
              {nav.subItems ? (
                <button
                  onClick={() => handleSubmenuToggle(index, menuType)}
                  className={`menu-item group ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? 'menu-item-active'
                      : 'menu-item-inactive'
                  } cursor-pointer ${
                    !isExpanded && !isHovered
                      ? 'lg:justify-center'
                      : 'lg:justify-start'
                  }`}
                >
                  <span
                    className={`${
                      openSubmenu?.type === menuType &&
                      openSubmenu?.index === index
                        ? 'menu-item-icon-active'
                        : 'menu-item-icon-inactive'
                    }`}
                  >
                    {nav.icon}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="menu-item-text">{nav.name}</span>
                  )}
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <ChevronDownIcon
                      className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                        openSubmenu?.type === menuType &&
                        openSubmenu?.index === index
                          ? 'rotate-180 text-brand-500'
                          : ''
                      }`}
                    />
                  )}
                </button>
              ) : (
                nav.path && (
                  <Link
                    to={nav.path}
                    className={`menu-item group ${
                      isActive(nav.path)
                        ? 'menu-item-active'
                        : 'menu-item-inactive'
                    }`}
                  >
                    <span
                      className={`relative inline-flex ${
                        isActive(nav.path)
                          ? 'menu-item-icon-active'
                          : 'menu-item-icon-inactive'
                      }`}
                    >
                      {nav.icon}
                    </span>
                    {(isExpanded || isHovered || isMobileOpen) && (
                      <span className="menu-item-text">{nav.name}</span>
                    )}
                  </Link>
                )
              )}
              {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
                <div
                  ref={(el) => {
                    subMenuRefs.current[`${menuType}-${index}`] = el;
                  }}
                  className="overflow-hidden transition-all duration-300"
                  style={{
                    height:
                      openSubmenu?.type === menuType &&
                      openSubmenu?.index === index
                        ? `${subMenuHeight[`${menuType}-${index}`]}px`
                        : '0px',
                  }}
                >
                  <ul className="mt-2 space-y-1 ml-9">
                    {nav.subItems.map((subItem) => (
                      <li key={subItem.name}>
                        <Link
                          to={subItem.path}
                          className={`menu-dropdown-item ${
                            isActive(subItem.path)
                              ? 'menu-dropdown-item-active'
                              : 'menu-dropdown-item-inactive'
                          }`}
                        >
                          {subItem.name}
                          <span className="flex items-center gap-1 ml-auto">
                            {subItem.new && (
                              <span
                                className={`ml-auto ${
                                  isActive(subItem.path)
                                    ? 'menu-dropdown-badge-active'
                                    : 'menu-dropdown-badge-inactive'
                                } menu-dropdown-badge`}
                              >
                                new
                              </span>
                            )}
                            {subItem.pro && (
                              <span
                                className={`ml-auto ${
                                  isActive(subItem.path)
                                    ? 'menu-dropdown-badge-active'
                                    : 'menu-dropdown-badge-inactive'
                                } menu-dropdown-badge`}
                              >
                                pro
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
      </ul>
    );
  };

  return (
    <aside
      className={`app-sidebar-surface fixed left-0 top-0 z-50 mt-16 flex h-screen flex-col px-4 text-gray-900 transition-all duration-300 ease-in-out dark:text-white lg:mt-0
        ${isExpanded || isMobileOpen ? 'w-[250px]' : isHovered ? 'w-[250px]' : 'w-[84px]'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`hidden border-b border-[rgb(var(--app-border)/0.64)] py-5 ${
          !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-center'
        } lg:flex`}
      >
        <Link
          to="/"
          className={`flex items-center ${
            isExpanded || isHovered || isMobileOpen ? 'px-2' : 'justify-center'
          }`}
        >
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img
                className="dark:hidden"
                src="/images/logo/logo-full.png"
                alt="Logo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo/logo-full.svg';
                }}
                style={{
                  height: '50px',
                  width: 'auto',
                  maxWidth: '210px',
                }}
              />
              <img
                className="hidden dark:block"
                src="/images/logo/logo-full-dark.png"
                alt="Logo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo/logo-full.svg';
                }}
                style={{
                  height: '50px',
                  width: 'auto',
                  maxWidth: '210px',
                }}
              />
            </>
          ) : (
            <>
              <img
                className="dark:hidden"
                src="/images/logo/logo-full.png"
                alt="Logo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo/logo-full.svg';
                }}
                style={{
                  height: '40px',
                  width: 'auto',
                  maxWidth: '40px',
                }}
              />
              <img
                className="hidden dark:block"
                src="/images/logo/logo-full-dark.png"
                alt="Logo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/logo/logo-full.svg';
                }}
                style={{
                  height: '40px',
                  width: 'auto',
                  maxWidth: '40px',
                }}
              />
            </>
          )}
        </Link>
      </div>
      <div className="no-scrollbar flex flex-1 flex-col overflow-y-auto px-1 pb-5 pt-4 duration-300 ease-linear">
        {/* Dual-mode users only (hotel IA §3); the 84px rail has no room. */}
        {(isExpanded || isHovered || isMobileOpen) && (
          <ExperienceSwitcher current="restaurant" surface="page" />
        )}
        <nav className="mb-5">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-3 flex text-[10px] font-semibold uppercase leading-[20px] tracking-[0.2em] text-[rgb(var(--app-muted)/0.78)] ${
                  !isExpanded && !isHovered
                    ? 'lg:justify-center'
                    : 'justify-start'
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  'Menu'
                ) : (
                  <HorizontaLDots />
                )}
              </h2>
              {renderMenuItems(navItems, 'main')}
            </div>
          </div>
        </nav>
        {/* {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null} */}
      </div>
    </aside>
  );
};

export default AppSidebar;
